/* eslint-disable @typescript-eslint/no-empty-interface */
import * as React from 'react';
import { useAtomValue } from 'jotai';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Checkbox, FormLabelWrapper } from '@restworld/ui-ds-base';
import { Variable, Worker } from '@restworld/utility-types';

import { EditModalFooter, EditModalTitle, IEditModalProps } from '../profile-edit-modal';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';
import { useApiSubmitHandler } from '../../hooks';
import { AssociationOptionLabel } from '../../../professional-associations';

interface IProfessionalAssociationsFormContentProps extends IEditModalProps {}

interface InputProps {
  professional_associations: string[];
}

/**
 * Lets a worker declare which professional associations they belong to (APCI
 * first). Membership is an attribute of the person, so it lives here rather
 * than among the job-search preferences.
 *
 * The options are not hardcoded: they come from the `professional_association`
 * variables, so a second association is a row in the database and not a change
 * to this file.
 *
 * The helper line under the field is required, not decorative. The declaration
 * leaves the profile and is shown to every employer who receives an
 * application, so the worker has to know that before they tick the box.
 */
export const ProfessionalAssociationsFormContent = ({
  onClose,
  editPageTitle,
}: IProfessionalAssociationsFormContentProps) => {
  const { t } = useTranslation();
  const { data: workerData } = useAtomValue(getWorkerAtom);
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);

  const defaultValues: InputProps = React.useMemo(
    () => ({
      professional_associations:
        workerData?.professional_associations
          ?.map((association) => association?.professional_association_var?.id)
          .filter(Boolean) ?? [],
    }),
    [workerData?.professional_associations]
  );

  const { control, handleSubmit, reset } = useForm<InputProps>({
    reValidateMode: 'onSubmit',
    defaultValues,
  });
  const { apiSubmitHandler, loading } = useApiSubmitHandler({
    onClose,
    editPageType: 'professional_associations',
  });

  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    (data) => {
      try {
        // Flat `{ professional_association: id }`, deliberately not wrapped in
        // `professional_association_var` the way `driving_licenses` is. The id
        // is the record's key on the backend, so this shape lets it recognise a
        // declaration the worker already made and leave its date alone. Wrapped,
        // every save would look like a brand new declaration.
        apiSubmitHandler({
          professional_associations: data.professional_associations.map((id) => ({
            professional_association: id,
          })) as Worker['professional_associations'],
        });
      } catch (error) {
        console.error(error, `error in professional associations submit`);
      }
    },
    [apiSubmitHandler]
  );

  const associationsEl = React.useMemo(
    () => (
      <FormLabelWrapper id="professional_associations" label={t('common:professional_associations')}>
        <Controller
          key="professional_associations"
          name="professional_associations"
          rules={{ required: false }}
          control={control}
          render={({ field }) => (
            // Built from the Checkbox primitive rather than CheckboxGroup: the
            // group renders `label` only and drops `icon`, and the logo beside
            // the name is the whole point of the picker.
            <ul className="flex flex-col gap-3 w-full m-0 p-0 list-none">
              {(workerFieldOptionsData?.['professionalAssociations'] ?? []).map((association: Variable) => (
                <li key={association.id}>
                  <Checkbox
                    id={association.id}
                    mode="clickable"
                    isChecked={(field.value ?? []).includes(association.id)}
                    title={<AssociationOptionLabel association={association} />}
                    onChange={(event) => {
                      const selected: string[] = field.value ? [...field.value] : [];
                      field.onChange(
                        event.target.checked
                          ? [...selected.filter((id) => id !== association.id), association.id]
                          : selected.filter((id) => id !== association.id)
                      );
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        />
        <p className="m-0 mt-2 text-sm text-gray-600">{t('common:professional_associations_visibility_helper')}</p>
      </FormLabelWrapper>
    ),
    [control, workerFieldOptionsData, t]
  );

  return (
    <div className="w-full">
      <EditModalTitle editPageTitle={editPageTitle} onClose={onClose} />
      <form autoComplete="off" method="post" onSubmit={handleSubmit(onSubmit)} className="flex gap-4 flex-col">
        {associationsEl}
        <EditModalFooter
          loading={loading}
          onClose={() => {
            reset(defaultValues);
            onClose();
          }}
          editPageType="professional_associations"
        />
      </form>
    </div>
  );
};

export default ProfessionalAssociationsFormContent;
